import RequestStatus from "../model/RequestStatus";
import {
    ADDITIONAL_NEW_PROGRAM_FEES_QUERY_START,
    NEW_PROGRAM_FEES_QUERY_FAILED,
    NEW_PROGRAM_FEES_QUERY_START,
    NEW_PROGRAM_FEES_QUERY_SUCCESS,
    UPDATE_NEW_PROGRAM_FEES_QUERY_START,
} from "src/actions/NewProgramFeesQueryAction";
import {GetProgramFeesResponse, ProgramFeeResult, ProgramFeeResultWithStatus, ProgramFees, ProgramFeesStore} from "src/model/ProgramFees";
import {
    ADDITIONAL_PROGRAM_FEES_QUERY_START,
    GetProgramFeesQueryComplete,
    PROGRAM_FEES_QUERY_FAILED,
    PROGRAM_FEES_QUERY_START,
    PROGRAM_FEES_QUERY_SUCCESS,
    UPDATE_PROGRAM_FEES_QUERY_START,
} from "src/actions/ProgramFeesQueryAction";
import {SWITCH_AMAZON_STORE_START, SWITCH_AMAZON_STORE_START_NEW} from "src/actions/SelectedProductQueryAction";

const initialState: GetProgramFeesResponse = {
    requestStatus: RequestStatus.NOT_LOADED,
    programFeesStore: null,
};

export default (
    state: GetProgramFeesResponse = initialState,
    action: GetProgramFeesQueryComplete
): GetProgramFeesResponse => {
    switch (action.type) {
        case NEW_PROGRAM_FEES_QUERY_START:
        case PROGRAM_FEES_QUERY_START:
        case SWITCH_AMAZON_STORE_START:
            return {
                requestStatus: RequestStatus.LOADING,
                programFeesStore: null,
            };
        case UPDATE_NEW_PROGRAM_FEES_QUERY_START:
        case UPDATE_PROGRAM_FEES_QUERY_START:
            const updatedProgramFeesStore =
                updateProgramsStatus(state.programFeesStore, action.programIdList, RequestStatus.UPDATING)
            return {
                requestStatus: RequestStatus.SUCCESS,
                programFeesStore: updatedProgramFeesStore,
            };
        case NEW_PROGRAM_FEES_QUERY_SUCCESS:
        case PROGRAM_FEES_QUERY_SUCCESS:
            const programFeesStore: ProgramFeesStore =
                mergeProgramFeesStore(state.programFeesStore, action.programFees)
            return {
                requestStatus: RequestStatus.SUCCESS,
                programFeesStore: programFeesStore,
            };
        case ADDITIONAL_PROGRAM_FEES_QUERY_START:
        case ADDITIONAL_NEW_PROGRAM_FEES_QUERY_START:
            const programFeesStoreWithLoadingPrograms =
                addAdditionalPrograms(state.programFeesStore, action.programIdList, RequestStatus.LOADING)
            return {
                requestStatus: RequestStatus.SUCCESS,
                programFeesStore: programFeesStoreWithLoadingPrograms,
            };
        case NEW_PROGRAM_FEES_QUERY_FAILED:
        case PROGRAM_FEES_QUERY_FAILED:
            const programFeesStoreWithErrorPrograms =
                addAdditionalPrograms(state.programFeesStore, action.programIdList, RequestStatus.ERROR)
            return {
                requestStatus: state.programFeesStore == null ? RequestStatus.ERROR : state.requestStatus,
                programFeesStore: programFeesStoreWithErrorPrograms,
            };
        case SWITCH_AMAZON_STORE_START_NEW:
            return {
                ...state,
                requestStatus: RequestStatus.NOT_LOADED
            };
        default:
            return state;
    }
};


/**
 * Update programs' status within application store
 * @param programFeesStore programFees data in application store
 * @param programIdList programs to be update
 * @param requestStatus RequestStatus to be used to set for programs
 * @returns new ProgramFeesStore or null
 */
function updateProgramsStatus(programFeesStore: ProgramFeesStore, programIdList: Array<string>, requestStatus: RequestStatus): ProgramFeesStore {
    const programFeeResultWithStatusMap = programFeesStore.programFeeResultWithStatusMap;
    const res: Record<string, ProgramFeeResultWithStatus> = {}
    if (programFeeResultWithStatusMap != null) {
        for (const key in programFeeResultWithStatusMap) {
            if (programIdList.includes(key)) {
                res[key] = {
                    requestStatus: requestStatus,
                    programFeeResult: programFeeResultWithStatusMap[key].programFeeResult,
                }
            } else {
                res[key] = programFeeResultWithStatusMap[key];
            }
        }
    }
    return {
        ...programFeesStore,
        programFeeResultWithStatusMap: res,
    };
}

/**
 * Add programs into application store with specific RequestStatus
 * @param programFeesStore programFees data in application store
 * @param programIdList programs to be adding
 * @param requestStatus RequestStatus to be used to set for programs
 * @returns new ProgramFeesStore or null
 */
function addAdditionalPrograms(programFeesStore: ProgramFeesStore, programIdList: Array<string>, requestStatus: RequestStatus): ProgramFeesStore | null {
    if (programFeesStore == null) {
        return null;
    }
    const programFeeResultWithStatusMap = buildProgramFeeResultWithStatusMap(programIdList, requestStatus);
    const addedAdditionalLoadingPrograms = mergeProgramFeeResultWithStatusMap(programFeesStore.programFeeResultWithStatusMap, programFeeResultWithStatusMap);
    return {
        ...programFeesStore,
        programFeeResultWithStatusMap: addedAdditionalLoadingPrograms,
    };
}

function buildProgramFeeResultWithStatusMap(programIdList: Array<string>, requestStatus: RequestStatus) {
    const res: Record<string, ProgramFeeResultWithStatus> = {}
    programIdList.map((programId, index) => {
        res[programId] = {
            requestStatus: requestStatus,
            programFeeResult: null
        }
    });
    return res;
}

/**
 * Merge new programFees data into application store
 * @param programFeesStore programFees data in application store
 * @param programFees programFees data needs to be merged
 * @returns merged ProgramFeesStore
 */
function mergeProgramFeesStore(programFeesStore: ProgramFeesStore | null,
    programFees: ProgramFees): ProgramFeesStore {
    const addingProgramFeeResultWithStatusMap = convertToProgramFeeResultWithStatusMap(programFees.programFeeResultMap, RequestStatus.SUCCESS);
    var programFeeResultWithStatusMap;
    if (programFeesStore == null) {
        programFeeResultWithStatusMap = addingProgramFeeResultWithStatusMap;
    } else {
        programFeeResultWithStatusMap = mergeProgramFeeResultWithStatusMap(programFeesStore.programFeeResultWithStatusMap, addingProgramFeeResultWithStatusMap);
    }
    return {
        countryCode: programFees.countryCode,
        asin: programFees.asin,
        fnsku: programFees.fnsku,
        merchantId: programFees.merchantId,
        programFeeResultWithStatusMap: programFeeResultWithStatusMap,
    };
}

function convertToProgramFeeResultWithStatusMap(programFeeResultMap: Record<string, ProgramFeeResult>,
    requestStatus: RequestStatus): Record<string, ProgramFeeResultWithStatus> {
    const res: Record<string, ProgramFeeResultWithStatus> = {}
    for (const key in programFeeResultMap) {
        res[key] = {
            requestStatus: requestStatus,
            programFeeResult: programFeeResultMap[key]
        }
    }
    return res;
}

function mergeProgramFeeResultWithStatusMap(programFeeResultWithStatusMap: Record<string, ProgramFeeResultWithStatus>,
    addingProgramFeeResultWithStatusMap: Record<string, ProgramFeeResultWithStatus>): Record<string, ProgramFeeResultWithStatus> {
    const res: Record<string, ProgramFeeResultWithStatus> = {}
    if (programFeeResultWithStatusMap != null) {
        for (const key in programFeeResultWithStatusMap) {
            res[key] = programFeeResultWithStatusMap[key]
        }
    }
    if (programFeeResultWithStatusMap != null) {
        for (const key in addingProgramFeeResultWithStatusMap) {
            res[key] = addingProgramFeeResultWithStatusMap[key]
        }
    }
    return res;
}
